import React, { createRef } from 'react';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import * as yup from 'yup';
import { ObjectSchema, Shape } from 'yup';
import { ReactComponent as StatesIcon } from '../../../core/ui/assets/images/icons/state_icon.svg';
import { ReactComponent as GraphIcon } from '../../../core/ui/assets/images/icons/parameter-v-2.svg';

import {
    AlertAction,
    ConfigurationActions,
    EditAlertAction,
    FormActions,
    ProductActions,
    statesActions,
} from '../../../core/actions';
import {
    IAlertRule,
    IAlertRuleApi,
    IData,
    IError,
    IFilter,
    IFlatTreeUnit,
    IJoin,
    IMonitoringTreeUnit,
    IOptions,
    IOrder,
    IProduct,
    ISensor,
    IUnit,
} from '../../../core/interfaces';
import { Button, ConfirmDialog, InfoModal, Select, DeleteDialog } from '../../../core/ui/components';
import { RootState } from '../../../core/store';
import { selectProductByUnitId } from '../../../core/selectors/product/productsSelector';
import { selectAllUnitItemInTree } from '../../../core/selectors/configurationTree/configurationTreeSelector';
import { IAOptions } from './AlertFormAutocompleteSensorStates';
import AlertRule from './AlertRule';
import Grid from '@material-ui/core/Grid';
import './AlertForm.scss';

interface IFormValues {
    productName: string;
    sensors: ISensor[];
    sensorOrigin: ISensor[];
    rules: IAlertRule[];
}

interface IProps {
    errors: IError;
    formOpened: boolean;
    toggleForm: (opened: boolean, name: string) => void;
    model: IData | null;
    storeAlert: (data: null) => void;
    currentUnit?: IUnit | null;
    alert?: IProduct | null;
    unitInTree?: IMonitoringTreeUnit[];
    updateConfigurationTree: (type: string, item: number, data: { isMinimized: boolean, alertsEnabled: boolean }) => void;
    loadStates: (search?: string, order?: IOrder, join?: IJoin) => void;
    bulkStoreAlertRules: (alertsRules: IAlertRuleApi[]) => void;
    updateAlertRules: (alertsRules: IAlertRuleApi[]) => void;
    deleteAlertRules: (alertsRules: IAlertRuleApi[]) => void;
    loadTree: (trackerLogic?: boolean) => void;
    loadProduct: (search?: string, order?: IOrder, join?: IJoin, filter?: IFilter) => void;
    alertRules: IAlertRuleApi[] | undefined
}

interface IState {
    initialValues: IFormValues;
    selectedUnit: string | number | IOptions;
    selectedProduct: string | number | IOptions;
    unitLabel: string | null;
    productLabel: string | null;
    confirm: boolean;
    confirmInfo: boolean;
    submittedValue: IFormValues | null;
    selectedUnitRules: boolean;
    reloadedRules: boolean;
    deleteRuleIndex: number | null;
}

/**
 * Alert edit form
 *
 * @class AlertForm
 */
class AlertForm extends React.PureComponent<IProps & WithTranslation, IState> {
    
    childRef: React.RefObject<unknown>;

    /**
     * Constructor
     *
     * @param {Object} props
     */
    constructor(props: IProps & WithTranslation) {

        super(props);

        const { t, currentUnit, alert, alertRules } = this.props;

        this.childRef = createRef();

        // @ts-ignore
        this.childRef.current = [];

        this.state = {
            initialValues: {
                rules: alertRules ? this.alertRulesApiToFormData(alertRules) : [],
                productName: currentUnit?.name || alert?.name ? `${currentUnit?.name}${alert?.name ? ` - ${alert?.name}` : ''}` : '',
                sensors: currentUnit && currentUnit.data ? this.sensorToOptions(currentUnit.data) : [],
                sensorOrigin: currentUnit && currentUnit.data ? currentUnit.data : [],
            },
            selectedUnit: currentUnit ? currentUnit.id : '',
            selectedProduct: alert && alert.id ? alert.id : '',
            unitLabel: currentUnit ? currentUnit.name : null,
            productLabel: alert && alert.name ? alert.name : null,
            confirm: false,
            confirmInfo: false,
            submittedValue: null,
            selectedUnitRules: Boolean(currentUnit && currentUnit.id),
            reloadedRules: false,
            deleteRuleIndex: null,
        };

        yup.addMethod(yup.string, 'multipleEmails', function(this: yup.StringSchema, msg: string) {

            return this.test({
                name: 'multipleEmails',
                message: msg,
                test: (value) => {

                    if (value) {

                        const emails = value.split(',');

                        if (value.match(/\s/) && !value.match(',')) {
                            return false;
                        }

                        const emailSchema = yup.string().email();

                        for (const email of emails) {

                            if (!emailSchema.isValidSync(email.replace(/\s/g, ''))) {

                                return false;
                            }
                        }
                    }

                    return true;
                },
            });
        });
        
        const sensorsParam = this.props.currentUnit?.data;
        
        this.validationSchema = yup.object().shape({
            rules: yup
                .array()
                .of(yup.object().shape({
                    sensor: yup
                        .string()
                        .required(t('REQUIRED')),
                    states: yup
                        .array()
                        .of(yup.number()),
                    operand: yup
                        .string()
                        .required(t('REQUIRED')),
                    isValid: yup
                        .boolean()
                        ,
                    value: yup
                        .string()
                        .trim()
                        .test({
                            name: 'stateSensors',
                            exclusive: false,
                            params: { sensorsParam },
                            message: t('REQUIRED') as string,
                            test: function(value) {

                                return (value === 'none' || !isNaN(parseInt(value)));
                            },
                        })
                        .required(t('REQUIRED')),
                    time: yup
                        .number()
                        .min(10, t('THE_MINIMUM_VALUE_MUST_BE_GREATER_THAN_OR_EQUAL_TO', { value: 10 }))
                        .typeError(t('VALUE_MUST_BE_AN_INTEGER_NUMBER')),
                    ruleCauseContexts: yup.array(),
                    comment: yup.string()
                        .trim(),
                    send_notifications: yup.boolean(),
                    address: yup
                        .string()
                        .trim()
                        .when('send_notifications', {
                            is: true,
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            //@ts-ignore
                            then: yup.string().required(t('ADDRESS_IS_REQUIRED')).multipleEmails(t('ONE_OR_MORE_EMAILS_ARE_NOT_VALID')),
                        }),
                    subject: yup
                        .string()
                        .trim()
                        .when('send_notifications', {
                            is: true,
                            then: yup.string().required(t('SUBJECT_IS_REQUIRED')),
                        }),
                    message: yup
                        .string()
                        .trim()
                        .when('send_notifications', {
                            is: true,
                            then: yup.string().required(t('MESSAGE_IS_REQUIRED')),
                        }),
                    stateSensors: yup
                        .array()
                        .test( {
                                name: 'stateSensors',
                                exclusive: false,
                                params: { sensorsParam },
                                message: t('REQUIRED') as string,
                                test: function(stateSensors) {
                                    const currentSensor = sensorsParam && sensorsParam.find((s:ISensor)=> s.id === parseInt(this.parent.sensor));
                                    if (currentSensor?.sensorType !== 'graph'){
                                        return true;
                                    }

                                    return !(this.parent.ruleCauseContexts.length>0 && stateSensors.length ===0);
                                },
                            }),
                    currentSensor: yup.object()
                        .test({
                            name: 'sensor',
                            exclusive: false,
                            params: { sensorsParam },
                            message: t('REQUIRED') as string,
                            test: function(sensor) {

                                const currentSensor = sensorsParam && sensorsParam.find((s:ISensor)=> s.id === parseInt(this.parent.sensor));

                                if (!this.parent.currentSensor) {
                                    return !this.parent.currentSensor;
                                }

                                return !!currentSensor;
                            },
                        }),
                })),
            productName: yup.string(),
            sensors: yup.array(),
            sensorOrigin: yup.array(),
        });

        this.onConfirmDialog = this.onConfirmDialog.bind(this);

        this.handleCancel = this.handleCancel.bind(this);

        this.handleCancel = this.handleCancel.bind(this);

        this.handleCancelConfirmDialog = this.handleCancelConfirmDialog.bind(this);

        this.handleSubmit = this.handleSubmit.bind(this);

        this.selectChangeFunction = this.selectChangeFunction.bind(this);

        this.handleCloseInfoModal = this.handleCloseInfoModal.bind(this);

        this.onAddNewRule = this.onAddNewRule.bind(this);

        this.onDeleteRule = this.onDeleteRule.bind(this);

        this.onSave = this.onSave.bind(this);

        this.onConfirmDeleteModal = this.onConfirmDeleteModal.bind(this);
    }

    componentDidMount() {

        this.props.loadProduct('', { column: 'id', dir: 'asc' }, {
            table: [
                'alertRules',
                'alertRules.unit',
                'alertRules.sensor',
                'alertRules.ruleCauseContexts',
                'alertRules.ruleCauseContexts.cause',
                'alertRules.stateSensors',
                'category',
            ],
        }, undefined);

        this.props.loadStates('', { column: 'id', dir: 'asc' }, { table: ['causes'] });
    }

    componentDidUpdate(prevProps: Readonly<IProps & WithTranslation>, prevState: Readonly<IState>) {

        const { alertRules } = this.props;

        const { reloadedRules, initialValues } = this.state;

        if (prevState.selectedUnit !== this.state.selectedUnit) {

            this.updateSensorArray();
        }

        if (prevState.unitLabel !== this.state.unitLabel || prevState.productLabel !== this.state.productLabel) {

            this.updateProductName();

        }

        if (!reloadedRules) {

            const initRuleIds: number[] = [], initRuleSubjects: string[] = [];

            const alertRuleIds: number[] = [], alertRuleSubjects: string[] = [];

            initialValues.rules.forEach((rule) => {

                if (rule.id) {
                    initRuleIds.push(rule.id);

                    initRuleSubjects.push(rule.subject || '');
                }
            });

            if (alertRules) {
                alertRules.forEach((rule) => {

                    if (rule.id) {
                        alertRuleIds.push(rule.id);
    
                        alertRuleSubjects.push(rule.subject || '');
                    }
                });
            }

            const equalIds = initRuleIds.sort().join(',') === alertRuleIds.sort().join(',');

            const equalSubjects = initRuleSubjects.sort().join(',') === alertRuleSubjects.sort().join(',');
            
            if (!equalIds || !equalSubjects) {

                this.setState({
                    initialValues: {
                        ...initialValues,
                        rules: alertRules ? this.alertRulesApiToFormData(alertRules) : [],
                    },
                    reloadedRules: true,
                });
            }
        }
    }

    componentWillUnmount() {

        this.props.storeAlert(null);

    }

    /**
     * Form validation schema
     *
     * @type {ObjectSchema}
     */
    private readonly validationSchema: ObjectSchema<Shape<object, { sensorOrigin: unknown[]; productName: string; rules: Shape<object, { states: number[]; subject: string; message: string; ruleCauseContexts: unknown[]; send_notifications: boolean; stateSensors: unknown[]; address: string; comment: string; sensor: string; operand: string; time: number; value: string }>[]; sensors: unknown[] }>>;

    sensorToOptions(data: ISensor[]) {
        const sensors: any[] = [];

        if (data) {

            data.sort((a, b) => {
                if (a.sensorType > b.sensorType) {
                    return 1;
                } 

                if (a.sensorType < b.sensorType) {
                    return -1;
                }

                return 0;
            }).forEach((value: ISensor) => {
                sensors.push({
                    value: value.id,
                    label: value.name,
                    icon: value.sensorType === 'state'? <StatesIcon className="option-icon"/> : <GraphIcon className="option-icon" />
                });
            });
        }

        return sensors;
    }

    /**
     * Alert form submit handler
     *
     * @param {IFormValues} values
     */
     handleSubmit(values: IFormValues) {

        const { unitInTree, currentUnit, alert } = this.props,
            { selectedUnit } = this.state;

        const unitIndex = unitInTree?.findIndex(unit => unit.id === (currentUnit?.id || selectedUnit));

        const unitForUpdate = unitInTree && unitIndex !== undefined ? unitInTree[unitIndex] : null;

        if (unitForUpdate) {

            if (!alert) {

                this.setState({
                    confirm: !unitForUpdate.alertsEnabled,
                    submittedValue: values,
                    confirmInfo: unitForUpdate.alertsEnabled,
                });

            } else {
                this.setState({
                    confirm: !!alert,
                    submittedValue: values,
                });
            }
        }
    }

    /**
     * User form cancel handler
     */
    handleCancel() {

        this.props.toggleForm(this.props.formOpened, '');

    }

    /**
     * User form cancel handler
     */
    handleCloseInfoModal() {

        this.setState({ confirmInfo: false });

    }

    /**
     * User form cancel handler
     */
    handleCancelConfirmDialog() {

        this.setState({ confirm: false });

    }

    selectedDataUnit(items: IMonitoringTreeUnit[]): IOptions[] {
        const data: IOptions[] = [];
        items.forEach(value => {
            data.push({
                label: value.name,
                value: value.id,
            });
        });

        return data;
    }

    updateSensorArray() {

        const { selectedUnit, initialValues } = this.state;
        const { unitInTree } = this.props;

        if (unitInTree) {
            unitInTree.forEach(value => {
                if (value.id === selectedUnit) {

                    initialValues.sensors = value.data;
                }
            });
        }
    }

    alertRulesToSendData(alertRules: IAlertRule[]): IAlertRuleApi[] {

        return alertRules.map(value => {
            return {
                id: value.id || 0,
                sensor: Number(value.sensor),
                operator: value.operand === 'null' ? null : value.operand,
                value: value.value !== 'none' ? Number(value.value.replace(/[^\d.-]/g, '')): null,
                threshold: Number(value.time) > 0 ? Number(value.time) : 10,
                unit: this.state.selectedUnit,
                product: this.props.alert!.id,
                sendMail: value.send_notifications,
                sendWhen: value.send_when,
                mailBody: value.message,
                subject: value.subject,
                emails: value.address,
                comment: value.comment,
                isValid: value.isValid,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                causes: value.ruleCauseContexts ? value.ruleCauseContexts.map(status => status?.cause?.id || status?.id || status) : [],
                stateSensors: value.stateSensors.map(value1 => parseInt(value1.value.toString())) || [],
            };
        }) as unknown as IAlertRuleApi[];
    }

    /**
     * Convert item to select options
     *
     * @param item
     * @return {IOptions}
     */
    itemToOption (item: ISensor): IAOptions {

        const itemType = item.sensorType === 'graph' ? 'Sensor' : 'State';

        return { value: item.id, label: item.name, type: itemType };
    }

    alertRulesApiToFormData(alertRules: IAlertRuleApi[]): IAlertRule[] {

        return alertRules.map(value => {
            return {
                id: value.id,
                sensor: value.sensor && 'id' in value.sensor ? value.sensor.id as number : '',
                states: [],
                operand: value.operator === null ? 'null': value.operator,
                value: String(value.value).replace(/[^\d.-]/g, '') || 'none',
                time: value.threshold,
                ruleCauseContexts: value.ruleCauseContexts || [],
                comment: value.comment || '',
                send_notifications: value.sendMail,
                send_when: value.sendWhen,
                address: value.emails || '',
                subject: value.subject || '',
                message: value.mailBody || '',
                currentSensor: value.sensor,
                isValid: value.isValid,
                stateSensors: (value.stateSensors as any).map(this.itemToOption) || [],
            };
        }) as unknown as IAlertRule[];
    }


    updateProductName() {

        const { initialValues, unitLabel, productLabel } = this.state;

        initialValues.productName = unitLabel ? `${unitLabel}${productLabel ? `: ${productLabel}` : ''}` : '';

        this.setState({ initialValues: initialValues });
    }

    /**
     * Handler create KPI
     */
    onConfirmDialog() {
        const {
                updateConfigurationTree,
                bulkStoreAlertRules,
                unitInTree,
                deleteAlertRules,
                updateAlertRules,
                alert,
                currentUnit,
                alertRules = [],
            } = this.props,
            { submittedValue, selectedUnit } = this.state;

        if (submittedValue && submittedValue.rules && submittedValue.rules.length === 0 && alertRules.length === 0) {

            if (unitInTree) {

                const unitIndex = unitInTree?.findIndex(unit => unit.id === (currentUnit?.id || selectedUnit));
                const unitForUpdate = unitInTree && unitIndex !== undefined ? unitInTree[unitIndex] : null;

                if (unitForUpdate) {

                    updateConfigurationTree('unit', unitForUpdate.id, {
                        isMinimized: unitForUpdate.isMinimized,
                        alertsEnabled: true,
                    });
                }
            }

        } else if (submittedValue && alert) {

            const { rules = [] } = submittedValue;

            if (alertRules && alertRules.length !== 0 && (rules?.length > alertRules.length || rules?.length < alertRules.length || rules?.length === alertRules.length)) {
                //update delete create

                const differenceData = this.difference(this.alertRulesToSendData(this.alertRulesApiToFormData(alertRules)), this.alertRulesToSendData(rules));

                if (differenceData.modifiedArr.length > 0) {

                    updateAlertRules(differenceData.modifiedArr);
                }

                if (differenceData.deleteArr.length > 0) {

                    deleteAlertRules(differenceData.deleteArr);
                }

                if (differenceData.create.length > 0) {

                    bulkStoreAlertRules(differenceData.create);
                }
            }

            if (alertRules && rules?.length > 0 && alertRules.length === 0) {
                //create

                bulkStoreAlertRules(this.alertRulesToSendData(rules));
            }

        }

        this.props.toggleForm(this.props.formOpened, '');
    }

    difference(oldArr: IAlertRuleApi[], newArr: IAlertRuleApi[]) {

        const modifiedArr = Array.from(newArr).filter((newAlertApi) => oldArr.some((oldAlertApi) => newAlertApi.id === oldAlertApi.id &&
            newAlertApi !== oldAlertApi)),
            deleteArr = Array.from(oldArr).filter((newAlertApi) => !newArr.some((oldAlertApi) => newAlertApi.id === oldAlertApi.id)),
            crateArr = Array.from(newArr).filter(value => !value.id || value.id === 0);

        return {
            modifiedArr: modifiedArr,
            deleteArr: deleteArr,
            create: crateArr,
        };
    }

    selectChangeFunction(event: React.ChangeEvent<Record<string, unknown>>) {

        this.setState({
            selectedUnit: event.target.value as string,
            selectedProduct: '',
            unitLabel: event.currentTarget.innerText as string,
            productLabel: null,
        });
    }

    onAddNewRule() {
    
        const rule = {
            sensor: '',
            operand: '<',
            value: '',
            time: 60,
            ruleCauseContexts: [],
            comment: '',
            send_notifications: false,
            address: '',
            subject: '',
            isValid: true,
            message: this.state.initialValues.productName,
            createdAt: new Date(),
            stateSensors: [],
            states: [],
        };

        const { rules } = this.state.initialValues;

        this.setState({
            initialValues: {
                ...this.state.initialValues,
                rules: [...rules, rule],
            },
        });
    }

    onDeleteRule(index: number) {

        this.setState({deleteRuleIndex: index});
    }

    onConfirmDeleteModal(index: number) {

        const { initialValues } = this.state;

        const { rules } = initialValues;

        if (rules[index]) {

            rules.splice(index, 1);

            this.setState({
                initialValues: {
                    ...initialValues,
                    rules: rules,
                },
                reloadedRules: true,
            });
        }
    }

    async onSave() {

        if (this.childRef.current) {

            const { initialValues } = this.state;

            let hasError = false;

            const data: IAlertRule[] = [];

             // @ts-ignore
            const childs = this?.childRef?.current.filter((el: any) => el);

            for await (const child of childs) {
                const ruleDetails = await child.getFormData();

                const { index } = ruleDetails;

                data[index] = ruleDetails.data;

                if (ruleDetails.hasError) {
                    hasError = ruleDetails.hasError;
                }
            }

            if (!hasError) {

                this.handleSubmit({
                    ...initialValues,
                    rules: data,
                });
            }
        }
    }

    /**
     * Render the component
     *
     * @return {JSX.Element}
     */
    render() {

        const { t, unitInTree, alert, currentUnit } = this.props,
            { initialValues, selectedUnit, selectedProduct, productLabel, unitLabel, selectedUnitRules, deleteRuleIndex } = this.state;

        const { productName, sensors, rules } = initialValues;

        return (
            <div className={'alert-form'}>
                <h2 className="form-header-heavy">{t('CONFIGURE_ALERT_RULES')}
                    <span>{unitLabel ? ` (${unitLabel}${productLabel?`: ${productLabel}` : ''})` : ''}</span>
                </h2>
                <div className="tip">{t(productLabel && unitLabel ?
                    'TO_SHOW_THIS_ALERTS_YOU_MUST_TURN_ON_THE_GRAPHS_FOR_THE_CORRESPONDING_PARAMS'
                    :
                    'YOU_WILL_CONFIGURE_RULES_AT_THE_PRODUCT_SECTION',
                )}
                </div>
                {!(alert) ?
                    <div className="select-wrap form-group select">
                        <Select
                            className={'form-field '
                            +
                            (selectedUnit ? 'success-field' : '')}
                            options={unitInTree ? this.selectedDataUnit(unitInTree) : []}
                            placeholder={t('SELECT_UNIT')}
                            label={t('SELECT_UNIT')}
                            value={selectedUnit}
                            displayEmpty
                            disabled={selectedUnitRules}
                            onChange={this.selectChangeFunction}
                            inputProps={{ 'aria-label': 'Without label' }}
                        />
                    </div>
                    :
                    null
                }
                {selectedProduct && selectedUnit ?
                    <React.Fragment>
                        <div className="row-subheader">{t('TRIGGER_THE_ALERT_IF')}</div>
                        <hr />
                    </React.Fragment>
                    :
                    null
                }
                <>
                    {rules.map((rule, index) => {
                        return (
                            <AlertRule 
                                // @ts-ignore
                                ref={(el: any) => this.childRef.current[index] = el}
                                key={index}
                                index={index}
                                sensors={sensors}
                                sensorsOrigin={currentUnit && currentUnit.data ? currentUnit.data : []}
                                productName={productName}
                                onDelete={this.onDeleteRule}
                                data={rule}
                            />
                        );
                    })}
                    {currentUnit?.alertsEnabled && 
                        <Grid container className="btn-add-holder">
                            <Button
                                type="button"
                                color="secondary"
                                variant="outlined"
                                onClick={this.onAddNewRule}
                            >
                                {t('ADD_RULE')}
                            </Button>
                        </Grid>
                    }
                    <div className="button-row flex-end space-2x" style={{ paddingBottom: 80 }}>
                        <div className="button-row flex-end space-2x">
                            <Button type="button" color="primary" onClick={this.handleCancel}>
                                {t('CANCEL')}
                            </Button>
                            <Button 
                                type="submit"
                                color="secondary"
                                disabled={!unitLabel}
                                onClick={this.onSave}
                            >
                                {t('SAVE')}
                            </Button>
                        </div>
                    </div>
                </>
                <ConfirmDialog
                    heading={this.props.t('DO_YOU_ACKNOWLEDGE_ALERT_CHANGES?')}
                    onAccept={this.onConfirmDialog}
                    onClose={this.handleCancelConfirmDialog}
                    open={this.state.confirm}
                />
                <DeleteDialog
                    open={deleteRuleIndex !== null}
                    removeId={deleteRuleIndex}
                    heading={t('REMOVE_THE_RULE')}
                    body={t('ARE_YOU_SURE_YOU_WANT_TO_REMOVE_THE_RULE')}
                    onAccept={this.onConfirmDeleteModal}
                    onClose={() => this.setState({deleteRuleIndex: null})}
                />
                <InfoModal
                    onClose={this.handleCloseInfoModal}
                    open={Boolean(this.state.confirmInfo)}
                >
                    {this.props.t('YOU_ALREADY_HAVE_AN_ALERT_FOR_THIS_UNIT')}
                </InfoModal>
            </div>
        );
    }
}

/**
 * Map global state to component props
 *
 * @param {Object} state
 *
 * @return {Object}
 */
const mapStateToProps = (state: RootState) => {

    const { form, editAlert } = state;

    const { formOpened } = form,
        { unit, alert } = editAlert;
    const errors = {};

    const currentProductAlert = alert && selectProductByUnitId(state, alert.id!);
    let alertRules: IAlertRuleApi[] | undefined = [];

    if (currentProductAlert) {

        alertRules =  currentProductAlert.alertRules
            ?.filter(alertRule => (alertRule.unit as IUnit).id === unit?.id)
            ?.sort((a, b) => {
                return new Date(a.createdAt!).getTime() < new Date(b.createdAt!).getTime() ? -1 : 1;
            });

    }

    const unitInTree = selectAllUnitItemInTree(state) as IFlatTreeUnit[];

    return {
        formOpened,
        errors,
        currentUnit: unit,
        alert: currentProductAlert,
        unitInTree,
        alertRules,
    };
};

/**
 * Map dispatch to component props
 *
 * @type {object}
 */
const mapDispatchToProps = ({
    toggleForm: FormActions.toggle,
    storeAlert: EditAlertAction.store,
    updateConfigurationTree: ConfigurationActions.updateAndRefresh,
    loadStates: statesActions.list,
    bulkStoreAlertRules: AlertAction.bulkStore,
    updateAlertRules: AlertAction.bulkUpdate,
    deleteAlertRules: AlertAction.bulkDelete,
    loadTree: ConfigurationActions.list,
    loadProduct: ProductActions.list,
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(AlertForm));